import React, { useState, useEffect, useRef } from 'react';
import { useData } from "../backend/firebase";
import { Link } from 'react-router-dom'; 
import { DateTime } from 'luxon';
import MultiStreamPlayer from '../components/MultiStreamPlayer';
import BridgeBrawlLogo from '../components/BridgeBrawlLogo';
import { StreamEmbed } from '../components/StreamEmbed';
import GoFundMeEmbed from '../components/GoFundMeEmbed';
import SEO from '../components/SEO';
import { getImagePath } from '../utils/imageRegistry';
import CreatorCarousel from '../components/CreatorCarousel';

const Home = () => {
  const { schedule, teams, homeContent, navigation, userRole } = useData();
  
  const [displayList, setDisplayList] = useState([]);
  const [listTitle, setListTitle] = useState("Upcoming Events");
  const [heroEvent, setHeroEvent] = useState(null);
  
  const [tick, setTick] = useState(0);

  const [isInsideIntro, setIsInsideIntro] = useState(true);
  const introRegionRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
        if (window.innerWidth > 900) return;
        if (!introRegionRef.current) return;

        // Get the bottom position of the intro text section
        const rect = introRegionRef.current.getBoundingClientRect();
        
        // If the bottom of the text is 100px above the bottom of the screen, fade out.
        // This ensures the widget vanishes before it overlaps the schedule.
        const isBottomStillVisible = rect.bottom > (window.innerHeight) && rect.top < (window.innerHeight-350);
        
        setIsInsideIntro(isBottomStillVisible);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    // Run once on mount to set initial state
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);


  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(timer);
  }, []);

  const now = DateTime.now();

    const getCountdown = (isoTime) => {
        const now = DateTime.now();
        const eventTime = DateTime.fromISO(isoTime, { zone: 'America/Los_Angeles' }).toLocal();
        const diff = eventTime.diff(now, ['days', 'hours', 'minutes']).toObject();

        if (diff.days > 1) return `in ${Math.round(diff.days)} days`;
        if (diff.days === 1) return `in 1 day`;
        if (diff.hours > 0) return `in ${Math.round(diff.hours)}h ${Math.round(diff.minutes)}m`;
        return `in ${Math.round(diff.minutes)}m`;
    };

    const TeamIcon = ({ teamId, size = 40 }) => {
        const team = teams.find(t => t.id.toString() === teamId?.toString());
        
        const style = {
            width: size,
            height: size,
            borderRadius: '50%',
            
            // REMOVED: border and box-shadow
            
            background: '#1a1a1a', // Dark background for transparent SVGs
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            flexShrink: 0
        };

        const imgStyle = {
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '50%'
        };

        if (!team) return <div style={style}></div>;

        if (team.logo) {
            const dataUrl = `data:image/svg+xml;utf8,${encodeURIComponent(team.logo)}`;
            return <div style={style}><img src={dataUrl} alt="logo" style={imgStyle} /></div>;
        }

        if (team.logoUrl) {
            return <div style={style}><img src={getImagePath(team.logoUrl)} alt="logo" style={imgStyle} /></div>;
        }

        // Fallback: Just show the team color if no image exists
        return <div style={{...style, background: team.color || '#333'}}></div>;
  };

  useEffect(() => {
    const fifteenMinsAgo = now.minus({ minutes: 15 });
    const oneMonthOut = now.plus({ months: 1 });

    const upcomingAndLive = schedule.filter(e => {
        const start = DateTime.fromISO(e.startTime);
        const end = DateTime.fromISO(e.endTime);
        return end > fifteenMinsAgo && start < oneMonthOut;
    }).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));

    if (upcomingAndLive.length > 0) {
        setDisplayList(upcomingAndLive.slice(0, 10));
        setListTitle("Upcoming & Live");
        // Auto-select live event if not already selected
        const liveOne = upcomingAndLive.find(e => now >= DateTime.fromISO(e.startTime) && now <= DateTime.fromISO(e.endTime));
        if(liveOne && !heroEvent) setHeroEvent(liveOne);
    } else {
        setDisplayList([]); 
        setListTitle("Upcoming Events");
    }
  }, [schedule, tick]); 

  const sections = homeContent?.sections || [];
  const safeSections = sections.length > 0 ? sections : (
      (homeContent?.welcomeVideo || homeContent?.welcomeText) ? [
          ...(homeContent.welcomeVideo ? [{id:'v', type:'media', content:homeContent.welcomeVideo, visible:true}] : []),
          ...(homeContent.welcomeText ? [{id:'t', type:'card', content:homeContent.welcomeText, visible:true}] : [])
      ] : []
  );
  const carouselSection = safeSections.find(s => s.type === 'carousel' && s.visible);
  const topSections = safeSections.filter(s => s.type !== 'carousel');

  const isScheduleVisible = (navigation.find(n => n.link === '/schedule')?.visible !== false) || (userRole?.role === 'admin');

  return (
    <div className="PageLayout" style={{flexDirection: 'column', overflowY: 'auto'}}>
        <SEO title="Home" isEvent={true} />
        <style>{`
            .paired-text-wrapper {
                position: relative; 
                flex: 999 1 300px;  
                min-height: 250px;
                /* NEW: This turns the wrapper into a smart container we can measure */
                container-type: size; 
            }
            .paired-text-card {
                position: absolute; 
                top: 0; left: 0; right: 0; bottom: 0;
                overflow: hidden !important; /* Forces it to NEVER scroll */
            }
            @media (max-width: 900px) {
                .paired-text-wrapper {
                    container-type: inline-size; /* Switches to width-scaling on mobile */
                }
                .paired-text-card {
                    position: relative; 
                    height: auto;
                }
            }

            /* --- SMART SCALING TYPOGRAPHY --- */
            .paired-rich-text {
                color: #E0E0E0;
                text-align: center;
                line-height: 1.3;
                /* Scales the base text based on Container Height (cqh) */
                font-size: clamp(10px, 6cqh, 16px); 
            }
            
            .paired-rich-text h1, .paired-rich-text h2, .paired-rich-text h3 {
                margin-top: 0.2em;
                margin-bottom: 0.4em;
                /* Shrinks the giant headers drastically if the video is small */
                font-size: clamp(14px, 12cqh, 34px); 
            }
            
            .paired-rich-text p {
                margin-bottom: 0.5em;
            }

            .home-header-grid {
                display: grid; 
                grid-template-columns: 1fr auto 1fr; 
                align-items: center; 
                padding: 40px 20px 30px 20px; 
                width: 100%;
                max-width: 1400px; 
                margin: 0 auto;
            }
            .left-spacer { display: block; }
            .prize-widget-container {
                display: flex; 
                justify-content: flex-end;
            }
            .prize-widget-scaler {
                transform: scale(0.6) translateX(-60px) translateY(-35px); 
                transform-origin: right center; 
                width: 350px;
                transition: opacity 0.25s ease-out;
            }
        

            @media (max-width: 900px) {
                .home-header-grid {
                    display: flex; /* Destroys the grid so logo centers perfectly */
                    justify-content: center;
                    padding: 20px 20px 10px 20px;
                }
                .left-spacer {
                    display: none; /* Hide empty column */
                }
                .prize-widget-container {
                    position: fixed;
                    bottom: 10;
                    bottom: env(safe-area-inset-bottom, 10);
                    right: 5;
                    z-index: 9999;
                    pointer-events: none; /* Block clicks when faded out */
                    will-change: transform, opacity;
                }
                .prize-widget-scaler {
                    transform: scale(0.45); 
                    transform-origin: bottom right;
                    opacity: 0; /* Hidden by default on mobile */
                    transition: opacity 0.3s ease-out;
                }
                /* This class gets added via JS when we are in the "Safe Zone" */
                .prize-widget-container.is-visible {
                    pointer-events: auto;
                }
                .prize-widget-container.is-visible .prize-widget-scaler {
                    opacity: 1;
                }
            }
        `}</style>
        {/* HEADER GRID: Logo Centered, Widget Right */}
        <div className="home-header-grid" style={{ flexShrink: 0 }}>
            
            {/* 1. Left Spacer (Empty to balance the grid on desktop) */}
            <div className="left-spacer"></div>

            {/* 2. Center Logo */}
            <div style={{display: 'flex', justifyContent: 'center', filter: 'drop-shadow(0px 10px 20px rgba(0,0,0,0.5))', transform: 'translateZ(0)', WebkitTransform: 'translateZ(0)'}}>
                <BridgeBrawlLogo width={320} />
            </div>

            {/* 3. Right Widget (Prize Pool) */}
            <div className={`prize-widget-container ${isInsideIntro ? 'is-visible' : ''}`}>
                <div className="prize-widget-scaler">
                    <GoFundMeEmbed type="medium" />
                </div>
            </div>
        </div>

        <div className="ContentWrapper" style={{paddingTop: 10, height: 'auto', overflow: 'visible', maxWidth: 1400, margin: '0 auto', width: '100%'}}>
            
            {/* --- TOP: HOME CONTENT --- */}
            <div ref={introRegionRef} style={{display: 'flex', flexDirection: 'column', gap: 30, marginBottom: 40}}>
    
                {safeSections.length === 0 && (
                    <div className="GlassCard" style={{padding: 40, display:'flex', alignItems:'center', justifyContent:'center', color:'#8F9EB5', minHeight: 200}}>
                        Welcome to Bridge Brawl
                    </div>
                )}

                {topSections.reduce((acc, section, index, array) => {
                    // 1. If this card was already grouped with the previous video, skip it
                    if (index > 0 && array[index - 1].type === 'media' && section.type === 'card') {
                        return acc;
                    }

                    // 2. Look ahead: Is this a media item followed immediately by a card?
                    const nextSection = array[index + 1];
                    if (section.type === 'media' && nextSection?.type === 'card') {
                        const isVideo = section.content.includes('youtube') || section.content.includes('twitch') || section.content.includes('youtu.be');
                        
                        acc.push(
                            <div key={`pair-${section.id}`} style={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: 30, 
                                width: '100%' 
                            }}>
                                
                                {/* LEFT: Video Component (DICTATES THE HEIGHT) */}
                                {/* max-width 600 ensures it is the exact size as before, but left-aligned */}
                                <div style={{ flex: '1 1 400px', maxWidth: 600 }}> 
                                    {isVideo ? (
                                        // Removed height: '100%' - this ensures the aspect ratio is never broken again
                                        <div style={{ width: '100%', aspectRatio: '16/9', background:'black', borderRadius: 16, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
                                            <StreamEmbed link={section.content} />
                                        </div>
                                    ) : (
                                        <img src={section.content} alt="" style={{ width: '100%', aspectRatio: '16/9', objectFit: 'cover', borderRadius: 16, boxShadow: '0 10px 30px rgba(0,0,0,0.3)' }} />
                                    )}
                                </div>

                                {/* RIGHT: Text Card Component (MATCHES THE HEIGHT) */}
                                <div className="paired-text-wrapper">
                                    <div className="GlassCard paired-text-card" style={{ 
                                        padding: '5%', /* Percentage padding compresses slightly as the box shrinks */
                                        display: 'flex', 
                                        flexDirection: 'column', 
                                        /* REMOVED: justifyContent: 'center' (This was causing the top cut-off bug) */
                                        background: 'linear-gradient(145deg, #25283B 0%, rgba(20, 22, 32, 0.95) 100%)', 
                                        border: '1px solid rgba(255, 255, 255, 0.08)',
                                        boxShadow: '0 15px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)',
                                        overflow: 'hidden' /* Hard lock to prevent scrolling */
                                    }}>
                                        {/* SAFE CENTERING WRAPPER: Centers vertically without cutting off the top */ }
                                        <div style={{ margin: 'auto', width: '100%' }}>
                                            <div 
                                                className="RichTextContent paired-rich-text"
                                                dangerouslySetInnerHTML={{ __html: nextSection.content }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    } else {
                        // 3. Fallback: Render standalone items if they aren't a matched pair
                        if (section.type === 'card') {
                            acc.push(
                                <div key={section.id} className="GlassCard" style={{
                                    padding: 0, minHeight: 0, 
                                    background: 'linear-gradient(145deg, #25283B 0%, rgba(20, 22, 32, 0.95) 100%)', 
                                    border: '1px solid rgba(255, 255, 255, 0.08)',
                                    boxShadow: '0 15px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05)'
                                }}>
                                    <div style={{padding: 40, textAlign: 'center'}}>
                                        <div className="RichTextContent" style={{lineHeight: 1.6, color: '#E0E0E0', fontSize: 16}} dangerouslySetInnerHTML={{ __html: section.content }} />
                                    </div>
                                </div>
                            );
                        } else if (section.type === 'media') {
                            const isVideo = section.content.includes('youtube') || section.content.includes('twitch') || section.content.includes('youtu.be');
                            acc.push(
                                <div key={section.id} className="StandaloneMedia" style={{width: '100%', display: 'flex', justifyContent: 'center'}}>
                                    {isVideo ? (
                                        <div style={{width: '100%', maxWidth: 600, margin: '0 auto', aspectRatio: '16/9', background:'black', borderRadius: 16, overflow: 'hidden', boxShadow: '0 10px 30px rgba(0,0,0,0.5)'}}>
                                            <StreamEmbed link={section.content} />
                                        </div>
                                    ) : (
                                        <img src={section.content} alt="" style={{maxWidth: '100%', maxHeight: 600, borderRadius: 16, boxShadow: '0 10px 30px rgba(0,0,0,0.3)'}} />
                                    )}
                                </div>
                            );
                        }
                    }
                    return acc;
                }, [])}
            </div>

            {/* --- BOTTOM: SPLIT SCREEN (Schedule | Viewer) --- */}
            <div style={{display: 'flex', flexWrap: 'wrap', gap: 30, alignItems: 'flex-start'}}>
                
                {/* LEFT: SCHEDULE */}
                {isScheduleVisible && displayList.length > 0 && (
                    <div style={{flex: '1 1 350px', maxWidth: 450, minWidth: 300}}>
                         <div className="GlassCard" style={{padding: 20, display: 'flex', flexDirection: 'column', height: '100%', maxHeight: 530}}>
                            <div style={{paddingBottom: 15, marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                <h3 className="SectionTitle" style={{fontSize: 20, margin: 0}}>{listTitle}</h3>
                                <Link to="/schedule" style={{color: '#6C5DD3', fontSize: 13, textDecoration: 'none', fontWeight: 'bold'}}>&rarr; Full Schedule &larr;</Link>
                            </div>

                            <div className="CustomScroll" style={{display: 'flex', flexDirection: 'column', gap: 10, overflowY: 'auto', paddingRight: 5}}>
                                {displayList.map(event => {
                                    // 1. Data Setup
                                    const t1 = teams.find(t => t.id.toString() === event.team1?.toString());
                                    const t2 = teams.find(t => t.id.toString() === event.team2?.toString());
                                    const start = DateTime.fromISO(event.startTime, { zone: 'America/Los_Angeles' }).toLocal();
                                    
                                    // 2. Countdown
                                    const now = DateTime.now();
                                    const diff = start.diff(now, ['days', 'hours', 'minutes']).toObject();
                                    let timer = "LIVE";
                                    if (diff.days > 1) timer = `${Math.round(diff.days)} DAYS`;
                                    else if (diff.hours > 0) timer = `${Math.round(diff.hours)}H ${Math.round(diff.minutes)}M`;
                                    else if (diff.minutes > 0) timer = `${Math.round(diff.minutes)}M`;

                                    // 3. Colors
                                    const c1 = t1?.color || '#555';
                                    const c2 = t2?.color || '#555';

                                    return (
                                        <div 
                                            key={event.id} 
                                            onClick={() => setHeroEvent(event)}
                                            style={{
                                                pointerEvents: 'none',
                                                marginBottom: 15, 
                                                borderRadius: 12, 
                                                cursor: 'pointer',
                                                background: '#151520', 
                                                
                                                // Borders
                                                borderLeft: `5px solid ${c1}`,
                                                borderRight: `5px solid ${c2}`,
                                                borderTop: '1px solid rgba(255,255,255,0.1)',
                                                borderBottom: '1px solid rgba(255,255,255,0.1)',
                                                
                                                // GPU Fix
                                                transform: 'translateZ(0)',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                overflow: 'visible'
                                            }}
                                        >
                                            {/* --- HEADER --- */}
                                            <div style={{
                                                padding: '10px 15px',
                                                background: 'rgba(255,255,255,0.03)',
                                                borderBottom: '1px solid rgba(255,255,255,0.05)',
                                                display: 'flex', justifyContent: 'space-between',
                                                fontSize: 11, fontWeight: 'bold', color: '#8F9EB5', letterSpacing: 1,
                                                borderTopRightRadius: 12, 
                                                borderTopLeftRadius: 12
                                            }}>
                                                <span>{start.toFormat('MMM d, h:mm a').toUpperCase()}</span>
                                                <span style={{color: '#FFD700'}}>{timer}</span>
                                            </div>

                                            {/* --- BODY (Single Row Layout) --- */}
                                            <div style={{
                                                padding: '15px', 
                                                display: 'flex', 
                                                alignItems: 'center', // Vertically center everything
                                                justifyContent: 'space-between', // Push teams to edges
                                                gap: 10
                                            }}>
                                                
                                                {/* Team 1 (Left) */}
                                                <div style={{display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0}}>
                                                    <div style={{flexShrink: 0}}>
                                                        <TeamIcon teamId={t1?.id} size={40} />
                                                    </div>
                                                    <span style={{fontSize: 15, fontWeight: 'bold', color: 'white', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                        {t1?.name || "TBA"}
                                                    </span>
                                                </div>

                                                {/* VS Badge (Center - No Line) */}
                                                <div style={{
                                                    flexShrink: 0,
                                                    fontSize: 12, 
                                                    fontWeight: '900', 
                                                    color: 'rgba(255,255,255,0.3)', 
                                                    textTransform: 'uppercase',
                                                    padding: '0 5px'
                                                }}>
                                                    VS
                                                </div>

                                                {/* Team 2 (Right) */}
                                                <div style={{display: 'flex', alignItems: 'center', gap: 12, flex: 1, justifyContent: 'flex-end', minWidth: 0}}>
                                                    <span style={{fontSize: 15, fontWeight: 'bold', color: 'white', textAlign: 'right', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                                        {t2?.name || "TBA"}
                                                    </span>
                                                    <div style={{flexShrink: 0}}>
                                                        <TeamIcon teamId={t2?.id} size={40} />
                                                    </div>
                                                </div>

                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                {/* RIGHT: VIEWER (Only shows when an event is selected) */}
                {carouselSection && (
                    <div style={{flex: '999 1 500px', minWidth: 300, display: 'flex', maxHeight: 550}}>
                        <CreatorCarousel teams={teams} />
                    </div>
                )}
                {heroEvent && (
                    <div style={{flex: '999 1 500px', minWidth: 300}}>
                        <div className="GlassCard" style={{padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', height: 'auto'}}>
                            <div style={{background: 'rgba(0,0,0,0.2)', padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)'}}>
                                <span style={{fontWeight: 'bold', color: '#fff'}}>Now Watching: {heroEvent.title}</span>
                                <button onClick={() => setHeroEvent(null)} style={{background: 'transparent', border: 'none', color: '#8F9EB5', cursor: 'pointer', fontSize: 12}}>✕ Close Player</button>
                            </div>
                            <MultiStreamPlayer event={heroEvent} />
                        </div>
                    </div>
                )}

            </div>
        </div>
    </div>
  );
};

export default Home;